/**
 * V50 — Payment Submissions admin panel.
 *
 * Shows every payment screenshot upload from /pay-now. Admin can:
 *   • Preview the screenshot in a lightbox
 *   • Download the original (JPEG)
 *   • Mark as Verified / Rejected
 *   • Add internal notes
 *   • Delete a submission
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Loader2, CheckCircle2, XCircle, Clock, Eye, Download, Trash2, Phone,
    FileImage, MessageSquare, RefreshCw, X,
} from "lucide-react";
import { api, formatApiError } from "../../lib/api";

const STATUS_PILLS = {
    pending: { label: "Pending", icon: Clock, cls: "bg-amber-50 text-amber-800 border-amber-200" },
    verified: { label: "Verified", icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
    rejected: { label: "Rejected", icon: XCircle, cls: "bg-rose-50 text-rose-800 border-rose-200" },
};

// V50 — Render a payment screenshot as <img> via an authenticated blob fetch.
// Plain <img src=".../screenshot"> would 401 because the browser cannot attach
// the admin Bearer token automatically.
function AuthedImg({ id, className, alt, testid }) {
    const [url, setUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let revokeUrl = null;
        let cancelled = false;
        setLoading(true);
        api.get(`/admin/payment-submissions/${id}/screenshot`, { responseType: "blob" })
            .then((res) => {
                if (cancelled) return;
                const blob = res.data;
                const objectUrl = URL.createObjectURL(blob);
                revokeUrl = objectUrl;
                setUrl(objectUrl);
            })
            .catch(() => { if (!cancelled) setUrl(null); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => {
            cancelled = true;
            if (revokeUrl) URL.revokeObjectURL(revokeUrl);
        };
    }, [id]);
    if (loading) {
        return (
            <div className={`${className} flex items-center justify-center bg-slate-100`}>
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
        );
    }
    if (!url) {
        return (
            <div className={`${className} flex flex-col items-center justify-center bg-slate-100 text-slate-400 text-xs gap-1`}>
                <FileImage className="w-6 h-6" />
                Image unavailable
            </div>
        );
    }
    return <img src={url} alt={alt} className={className} data-testid={testid} />;
}

export default function PaymentSubmissionsPanel() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("");
    const [preview, setPreview] = useState(null);     // submission being previewed
    const [notesEdit, setNotesEdit] = useState({});   // { [id]: draftText }

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = statusFilter ? { status: statusFilter } : {};
            const res = await api.get("/admin/payment-submissions", { params });
            setItems(res.data?.items || []);
        } catch (err) {
            toast.error(formatApiError(err) || "Failed to load payment submissions");
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => { load(); }, [load]);

    const setStatus = async (id, status) => {
        try {
            await api.patch(`/admin/payment-submissions/${id}`, { status });
            toast.success(`Marked as ${status}`);
            setItems((rows) => rows.map((r) => (r.id === id ? { ...r, status } : r)));
        } catch (err) {
            toast.error(formatApiError(err) || "Update failed");
        }
    };

    const saveNotes = async (id) => {
        const admin_notes = notesEdit[id] ?? "";
        try {
            await api.patch(`/admin/payment-submissions/${id}`, { admin_notes });
            toast.success("Notes saved");
            setItems((rows) => rows.map((r) => (r.id === id ? { ...r, admin_notes } : r)));
            setNotesEdit((m) => ({ ...m, [id]: undefined }));
        } catch (err) {
            toast.error(formatApiError(err) || "Save failed");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this payment submission? This cannot be undone.")) return;
        try {
            await api.delete(`/admin/payment-submissions/${id}`);
            toast.success("Submission deleted");
            setItems((rows) => rows.filter((r) => r.id !== id));
        } catch (err) {
            toast.error(formatApiError(err) || "Delete failed");
        }
    };

    const screenshotUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/admin/payment-submissions/${id}/screenshot`;

    // Authenticated download (since plain anchor cannot send the Bearer token).
    const downloadScreenshot = async (sub) => {
        try {
            const res = await api.get(`/admin/payment-submissions/${sub.id}/screenshot`, {
                params: { download: 1 },
                responseType: "blob",
            });
            const blob = res.data;
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const safe = (sub.name || "payment").replace(/\s+/g, "_");
            a.download = `${safe}_${sub.id.slice(0, 8)}.jpg`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (err) {
            toast.error(formatApiError(err) || "Download failed");
        }
    };

    return (
        <div data-testid="admin-payment-submissions">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
                <div>
                    <h2 className="font-heading text-2xl font-bold">Payment Submissions</h2>
                    <p className="text-sm text-slate-500 mt-0.5">
                        Every payment screenshot uploaded from <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">/pay-now</code>. Preview, download, verify, or add notes.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="text-sm px-3 py-2 rounded-lg border border-slate-200 bg-white"
                        data-testid="payment-status-filter"
                    >
                        <option value="">All statuses ({items.length})</option>
                        <option value="pending">Pending</option>
                        <option value="verified">Verified</option>
                        <option value="rejected">Rejected</option>
                    </select>
                    <button
                        onClick={load}
                        className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200"
                        data-testid="payment-refresh"
                    >
                        <RefreshCw className="w-4 h-4" /> Refresh
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-16">
                    <Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A]" />
                </div>
            ) : items.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center" data-testid="payment-empty">
                    <FileImage className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <div className="text-slate-500 text-sm">No payment submissions yet.</div>
                </div>
            ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="payment-grid">
                    {items.map((it) => {
                        const pill = STATUS_PILLS[it.status] || STATUS_PILLS.pending;
                        const PillIcon = pill.icon;
                        return (
                            <div
                                key={it.id}
                                className="bg-white border border-slate-200 rounded-2xl overflow-hidden flex flex-col"
                                data-testid={`payment-card-${it.id}`}
                            >
                                <button
                                    onClick={() => setPreview(it)}
                                    className="relative aspect-[4/5] bg-slate-100 overflow-hidden group"
                                    data-testid={`payment-thumb-${it.id}`}
                                >
                                    <AuthedImg
                                        id={it.id}
                                        alt={`Payment screenshot from ${it.name}`}
                                        className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform"
                                    />
                                    <span className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold border bg-white/95 backdrop-blur-sm">
                                        <PillIcon className="w-3 h-3" /> {pill.label}
                                    </span>
                                    <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors">
                                        <Eye className="w-7 h-7 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </span>
                                </button>
                                <div className="p-3.5 flex-1 flex flex-col gap-2">
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <div className="font-semibold text-[#0F172A] truncate">{it.name}</div>
                                            <a
                                                href={`tel:${it.phone}`}
                                                className="inline-flex items-center gap-1 text-xs text-[#1E3A8A] hover:underline"
                                                data-testid={`payment-phone-${it.id}`}
                                            >
                                                <Phone className="w-3 h-3" /> {it.phone}
                                            </a>
                                        </div>
                                        {it.amount && (
                                            <span className="font-mono font-bold text-sm text-emerald-700 shrink-0">
                                                ₹{it.amount}
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-[11px] text-slate-500">
                                        {new Date(it.created_at).toLocaleString()}
                                    </div>
                                    {(it.reference || it.notes) && (
                                        <div className="text-xs text-slate-600 bg-slate-50 rounded-md p-2 space-y-0.5">
                                            {it.reference && (
                                                <div>
                                                    <span className="font-semibold">Ref:</span>{" "}
                                                    <code className="font-mono text-[11px]">{it.reference}</code>
                                                </div>
                                            )}
                                            {it.notes && <div><span className="font-semibold">Notes:</span> {it.notes}</div>}
                                        </div>
                                    )}
                                    <div className="flex items-center gap-1 mt-auto pt-2 border-t border-slate-100">
                                        <a
                                            href="#download"
                                            onClick={(e) => { e.preventDefault(); downloadScreenshot(it); }}
                                            className="flex-1 inline-flex items-center justify-center gap-1 text-xs px-2 py-1.5 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700"
                                            data-testid={`payment-download-${it.id}`}
                                        >
                                            <Download className="w-3.5 h-3.5" /> Download
                                        </a>
                                        <button
                                            onClick={() => setStatus(it.id, "verified")}
                                            disabled={it.status === "verified"}
                                            className="flex-1 inline-flex items-center justify-center gap-1 text-xs px-2 py-1.5 rounded-md bg-emerald-50 hover:bg-emerald-100 text-emerald-800 disabled:opacity-40"
                                            data-testid={`payment-verify-${it.id}`}
                                        >
                                            <CheckCircle2 className="w-3.5 h-3.5" /> Verify
                                        </button>
                                        <button
                                            onClick={() => setStatus(it.id, "rejected")}
                                            disabled={it.status === "rejected"}
                                            className="inline-flex items-center justify-center px-2 py-1.5 rounded-md bg-rose-50 hover:bg-rose-100 text-rose-700 disabled:opacity-40"
                                            data-testid={`payment-reject-${it.id}`}
                                            aria-label="Reject"
                                        >
                                            <XCircle className="w-3.5 h-3.5" />
                                        </button>
                                        <button
                                            onClick={() => remove(it.id)}
                                            className="inline-flex items-center justify-center px-2 py-1.5 rounded-md bg-slate-50 hover:bg-rose-50 text-slate-500 hover:text-rose-600"
                                            data-testid={`payment-delete-${it.id}`}
                                            aria-label="Delete"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                    {/* Admin notes */}
                                    <div className="mt-1">
                                        {notesEdit[it.id] !== undefined ? (
                                            <div className="space-y-1.5">
                                                <textarea
                                                    value={notesEdit[it.id]}
                                                    onChange={(e) => setNotesEdit((m) => ({ ...m, [it.id]: e.target.value }))}
                                                    rows={2}
                                                    className="w-full text-xs p-2 rounded-md border border-slate-200 focus:ring-2 focus:ring-[#3B82F6] focus:outline-none"
                                                    placeholder="Admin notes (visible to admins only)"
                                                />
                                                <div className="flex gap-1">
                                                    <button
                                                        onClick={() => saveNotes(it.id)}
                                                        className="text-xs px-2 py-1 rounded bg-[#1E3A8A] text-white"
                                                    >
                                                        Save
                                                    </button>
                                                    <button
                                                        onClick={() => setNotesEdit((m) => ({ ...m, [it.id]: undefined }))}
                                                        className="text-xs px-2 py-1 rounded bg-slate-100"
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <button
                                                onClick={() => setNotesEdit((m) => ({ ...m, [it.id]: it.admin_notes || "" }))}
                                                className="text-[11px] inline-flex items-center gap-1 text-slate-500 hover:text-[#1E3A8A]"
                                            >
                                                <MessageSquare className="w-3 h-3" />
                                                {it.admin_notes ? "Edit admin note" : "Add admin note"}
                                            </button>
                                        )}
                                        {it.admin_notes && notesEdit[it.id] === undefined && (
                                            <div className="text-[11px] text-slate-600 italic mt-1 line-clamp-2">
                                                “{it.admin_notes}”
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Lightbox preview */}
            {preview && (
                <div
                    role="dialog"
                    aria-modal="true"
                    className="fixed inset-0 z-[80] bg-slate-900/85 backdrop-blur-sm flex items-center justify-center p-4"
                    onClick={() => setPreview(null)}
                    data-testid="payment-preview-modal"
                >
                    <div
                        className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between p-4 border-b">
                            <div>
                                <div className="font-semibold text-[#0F172A]">{preview.name}</div>
                                <div className="text-xs text-slate-500">
                                    {preview.phone} · {new Date(preview.created_at).toLocaleString()}
                                </div>
                            </div>
                            <button
                                onClick={() => setPreview(null)}
                                className="p-2 hover:bg-slate-100 rounded-lg"
                                aria-label="Close"
                                data-testid="payment-preview-close"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-4">
                            <AuthedImg
                                id={preview.id}
                                alt={`Payment screenshot from ${preview.name}`}
                                className="w-full h-auto rounded-lg border border-slate-200"
                                testid="payment-preview-image"
                            />
                            <div className="mt-3 flex flex-wrap gap-2">
                                <button
                                    onClick={() => downloadScreenshot(preview)}
                                    className="inline-flex items-center gap-1.5 text-sm bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-lg"
                                >
                                    <Download className="w-4 h-4" /> Download original
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
